"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { Capacitor } from "@capacitor/core";
import { X } from "lucide-react";
import { LOCALE_PATTERN } from "@/i18n/routing";
import {
  APP_BANNER_DISMISS_KEY as DISMISS_KEY,
  APP_BANNER_HTML_ATTR as HTML_ATTR,
} from "./app-install-banner-script";

/**
 * 안드로이드 모바일 웹 방문자에게 Play 스토어 앱 설치를 유도하는 상단 배너.
 * (iOS 는 layout.tsx 의 네이티브 Smart App Banner 를 그대로 쓰므로 제외.)
 *
 * 노출 조건: ① 앱 내부(Capacitor WebView) 아님 ② 안드로이드 ③ 최근에 닫지 않음.
 * Play 스토어 페이지가 설치/열기를 자동 분기하므로 CTA 는 스토어 링크 하나로 충분.
 */
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.locarora.app";

/** "/{locale}" 또는 "/{locale}/" — 로케일 루트(홈). */
const LOCALE_HOME_RE = new RegExp(`^/(${LOCALE_PATTERN})/?$`);

export function AppInstallBanner() {
  const t = useTranslations("common");
  const pathname = usePathname();
  const [show, setShow] = useState(false);

  // 메인(홈/로케일 루트)에서만 노출. 다른 페이지(특히 검색/지도)는 뷰포트 기준
  // fixed 레이아웃이라 상단 flow 배너와 겹치고, 설치 유도엔 랜딩 페이지가 핵심.
  const isHome = pathname === "/" || LOCALE_HOME_RE.test(pathname);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return; // 앱 안에서는 숨김
    // 안드로이드 웹 + 이 세션(탭)에서 닫지 않음 — 첫 페인트 전 인라인 스크립트가 같은
    // 조건으로 단 속성을 그대로 따른다(자리 예약과 실제 노출이 어긋나지 않게).
    if (document.documentElement.getAttribute(HTML_ATTR) !== "1") return;
    // 노출 조건은 Capacitor/UA/sessionStorage 등 클라이언트 전용 값이라 SSR 단계에서
    // 판단할 수 없고 effect 에서만 결정 가능하다.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setShow(true);
  }, []);

  if (!isHome) return null;

  // 하이드레이션 전: 같은 높이의 빈 자리. 기본은 숨김이고 <html data-app-banner="1"> 일
  // 때만 보인다(globals.css) — 배너가 뜰 기기에서만 미리 자리를 잡는다.
  if (!show) {
    return (
      <div
        aria-hidden="true"
        data-app-banner-slot=""
        className="hidden h-[57px] border-b border-gray-200 bg-white"
      />
    );
  }

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore
    }
    document.documentElement.removeAttribute(HTML_ATTR);
    setShow(false);
  };

  return (
    <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-3 py-2">
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("appInstall.close")}
        className="-ml-1 shrink-0 p-1 text-gray-400"
        tabIndex={-1}
      >
        <X className="h-5 w-5" />
      </button>
      <Image
        src="/icons/android/icon-192.png"
        alt="LOCARORA"
        width={40}
        height={40}
        className="h-10 w-10 shrink-0 rounded-lg"
        // 로컬 /public 자산 — 정적 CDN 원본 서빙(Vercel 이미지 최적화 과금 제외).
        unoptimized
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-gray-900">
          {t("appInstall.title")}
        </p>
        <p className="truncate text-xs text-gray-500">
          {t("appInstall.subtitle")}
        </p>
      </div>
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 rounded-full bg-orange-500 px-4 py-1.5 text-sm font-semibold text-white"
      >
        {t("appInstall.cta")}
      </a>
    </div>
  );
}
